import React from "react";

/**
 * Long / short, as one segmented switch rather than two buttons.
 *
 * It was a pair of raised buttons where only the active side carried colour
 * and both were `disabled`, so the chosen half looked pressable and the other
 * half looked broken. Direction is the first decision on this ticket, so the
 * control reads as one switch with a side selected: a single track, a filled
 * thumb under the chosen side, both halves live.
 *
 * The arrow is not decoration. Green-up / red-down is the convention every
 * trading platform shares, and roughly 8% of men have a colour vision
 * deficiency that flattens exactly that pair — so direction is carried by the
 * glyph and the word as well as the fill, and never by the fill alone. The
 * same rule holds anywhere else on the ticket that a number is coloured by
 * sign.
 */
const SIDES = [
  { key: 0, label: "Buy / Long", arrow: "↑", cls: "is-long" },
  { key: 1, label: "Sell / Short", arrow: "↓", cls: "is-short" },
];

/**
 * The fill is a gradient in a class, never an inline colour.
 *
 * Dark mode runs a sweep over the whole tree that rewrites `background`
 * declarations to surface tokens with `!important` (theme/theme.css §B) —
 * right for the hundred cards written as literal whites, wrong for a semantic
 * fill whose colour *is* the state. A `background-image` survives that sweep,
 * and it is also what gives the thumb its slight lift.
 */
const SIDE_CSS = `
  /* The buttons paint nothing at all now — the thumb behind them is the fill.
     appearance: none matters: a <button> with no author background falls back
     to the UA's buttonface, which under color-scheme: dark is an opaque
     grey — that is what was covering the green thumb and leaving only its
     shadow glowing out from under the edges. Forced, because this control sits
     inside a page-wide dark sweep that rewrites backgrounds with !important. */
  .ce-side {
    -webkit-appearance: none;
    appearance: none;
    background-color: transparent !important;
    background-image: none;
  }
  .ce-side[aria-checked="false"]:hover { background-image: linear-gradient(180deg, rgba(127,127,127,.10), rgba(127,127,127,.10)); }

  /* The fill is one thumb that travels, not two fills that swap.

     Switching direction is the ticket's most consequential toggle, and an
     instant swap gives a trader no way to tell a mis-tap from an intended one
     — nothing moved, the colour was simply different afterwards. A thumb that
     slides across says which side it left as well as which side it landed on.

     One element also means one shadow and one gradient in flight rather than
     two cross-fading, which is what made the old swap look like a flicker. */
  .ce-side-thumb {
    position: absolute;
    top: 5px;
    bottom: 5px;
    left: 5px;
    /* Track = 2 cells + a 4px gap inside 5px padding, so a cell is
       (100% - 14px) / 2 — the same arithmetic the grid does. */
    width: calc(50% - 7px);
    border-radius: 12px;
    pointer-events: none;
    background-image: linear-gradient(180deg, #24A066 0%, #1F8B58 100%);
    box-shadow: 0 8px 18px rgba(31, 139, 88, .34), inset 0 1px 0 rgba(255, 255, 255, .3);
    /* Lands with a touch of overshoot: the switch settles rather than stops,
       which is what makes a fast move read as physical instead of abrupt. */
    transition: transform .26s cubic-bezier(.34, 1.56, .64, 1),
                background-image .18s ease,
                box-shadow .18s ease;
  }
  .ce-side-thumb.is-short {
    /* One cell plus the gap, measured on the thumb's own width. */
    transform: translateX(calc(100% + 4px));
    background-image: linear-gradient(180deg, #D93F44 0%, #C8353A 100%);
    box-shadow: 0 8px 18px rgba(200, 53, 58, .34), inset 0 1px 0 rgba(255, 255, 255, .3);
  }
  @media (prefers-reduced-motion: reduce) {
    .ce-side-thumb { transition: none; }
  }
`;

/*
 * `ce-raw` on the track opts this switch out of the dark-mode sweep
 * (theme/theme.css §B). That sweep exists to recolour the hundreds of cards
 * written as literal whites, and it has no business rewriting a control whose
 * fill *is* its state. The `var()` tokens below still resolve per theme —
 * `ce-raw` only exempts the sweep's !important overrides.
 */
const TradeDirection = ({ longShort, setLongShort }) => (
  <div
    role="radiogroup"
    aria-label="Direction"
    className="ce-raw grid grid-cols-2"
    style={{
      position: "relative",
      gap: 4,
      // A segmented control is a sunken track holding one raised thumb. The
      // track is recessed rather than bordered — nothing on this page carries
      // both a fill and a hairline.
      padding: 5,
      borderRadius: 16,
      background: "var(--surface-2)",
      boxShadow: "var(--inset)",
      border: 0,
    }}
  >
    <style>{SIDE_CSS}</style>
    <span aria-hidden="true" className={`ce-side-thumb${longShort === 1 ? " is-short" : ""}`} />
    {SIDES.map((side) => {
      const active = longShort === side.key;
      return (
        <button
          key={side.key}
          role="radio"
          aria-checked={active}
          onClick={() => setLongShort(side.key)}
          className={`ce-side ${side.cls} flex items-center justify-center`}
          style={{
            // Above the travelling thumb, which is painted on the track.
            position: "relative",
            zIndex: 1,
            gap: 6,
            height: 44,
            borderRadius: 12,
            border: "none",
            cursor: "pointer",
            fontFamily: "General Sans",
            fontWeight: 600,
            fontSize: 15,
            // #1F8B58 / #C8353A rather than the old #38A569 / #EB4245: white
            // text needs 4.5:1 against the fill to clear WCAG AA, and neither
            // of the previous two reached it. The selected colours live in
            // SIDE_CSS; this only sets the unselected case.
            color: active ? "#fff" : "var(--ink-2, #667085)",
            transition: "background-image .16s ease, color .16s ease, box-shadow .16s ease",
          }}
        >
          <span aria-hidden="true" style={{ fontSize: 15, lineHeight: 1 }}>{side.arrow}</span>
          {side.label}
        </button>
      );
    })}
  </div>
);

export default TradeDirection;
